/**
 * THE ONE LEGEND, AND IT IS A CONTROL.
 *
 * The analytics screen hand-built a `KeyRow` per panel and the reports screen
 * used the stock `<Legend wrapperStyle>`. Neither could be clicked, so a chart
 * with a series a reader did not care about had no way to put it aside.
 *
 * Here every entry is a `<button aria-pressed>`, and the toggle state lives in
 * `filter.seriesOff` as `<panelId>:<seriesKey>` — so it is in the URL, it
 * survives a tab switch, and it travels in a saved view. That is the whole
 * reason it is not component state.
 *
 * A HIDDEN SERIES IS ANNOUNCED, NOT MERELY FADED. It gets `aria-pressed`, a
 * strike-through and a drained swatch, because a series that is hidden and a
 * series that happens to be flat look identical otherwise.
 *
 * LABELS WEAR INK; THE CHIP CARRIES THE IDENTITY. Colour never travels alone —
 * every entry ships its name beside its swatch, which is also what makes the
 * legend usable to a reader who cannot separate two of the hues.
 */
import React from 'react';
import { LegendItem, LegendRow, LegendSwatch, LegendValue } from './chart.styled';

export interface LegendEntry {
  key: string;
  label: string;
  tone: string;
  /** Drawn as a dashed outline rather than a solid chip — the comparison
   *  period, and anything else that is a reference rather than a reading. */
  dashed?: boolean;
  /** A total beside the name. Optional: on a time series it is noise, on a
   *  composition it is the point. */
  value?: number;
  /** Some series cannot be turned off — a single-series chart's only series, or
   *  the one the panel exists to show. */
  fixed?: boolean;
}

export interface DashLegendProps {
  panelId: string;
  entries: LegendEntry[];
  /** The `<panelId>:<seriesKey>` list from the filter. */
  seriesOff: string[];
  onToggle: (seriesKey: string) => void;
  below?: boolean;
}

export const seriesToggleId = (panelId: string, seriesKey: string): string =>
  `${panelId}:${seriesKey}`;

export const isSeriesOff = (
  seriesOff: string[],
  panelId: string,
  seriesKey: string
): boolean => seriesOff.includes(seriesToggleId(panelId, seriesKey));

/** The keys this panel has hidden, for handing to a chart and to the tooltip. */
export const hiddenKeys = (seriesOff: string[], panelId: string): string[] =>
  seriesOff
    .filter(entry => entry.startsWith(`${panelId}:`))
    .map(entry => entry.slice(panelId.length + 1));

export const DashLegend: React.FC<DashLegendProps> = ({
  panelId,
  entries,
  seriesOff,
  onToggle,
  below,
}) => {
  // A legend of ONE entry explains nothing — the panel title already names the
  // series. This is the rule the hour-of-day and dwell panels applied by hand.
  if (entries.length < 2) return null;

  return (
    <LegendRow $below={below} role="group" aria-label="Chart series">
      {entries.map(entry => {
        const off = isSeriesOff(seriesOff, panelId, entry.key);

        return (
          <LegendItem
            key={entry.key}
            type="button"
            $off={off}
            aria-pressed={entry.fixed ? undefined : !off}
            disabled={entry.fixed}
            title={
              entry.fixed
                ? undefined
                : off
                ? `Show ${entry.label}`
                : `Hide ${entry.label}`
            }
            onClick={() => (entry.fixed ? undefined : onToggle(entry.key))}
          >
            <LegendSwatch
              tone={entry.tone}
              dashed={entry.dashed}
              $off={off}
              aria-hidden="true"
            />
            {entry.label}
            {entry.value != null ? (
              <LegendValue>{entry.value.toLocaleString()}</LegendValue>
            ) : null}
          </LegendItem>
        );
      })}
    </LegendRow>
  );
};
